<template>
  <q-form class="row q-col-gutter-lg">
    <div
      class="col-xs-12 col-md-grow"
      style="min-width: 250px"
    >
      <q-input
        ref="cardNumber"
        v-model="cardNumber"
        outlined
        autocomplete="cc-number"
        mask="#### - #### - #### - ####"
        type="tel"
        :label="$t('payment.cardNumber')"
        :rules="[
          v => !!v || $t('payment.validation.cardNumber.required'),
          v => v && v.length >= 12 || $t('payment.validation.cardNumber.required'),
          () => validateCardNumber || $t('payment.validation.cardNumber.invalid')
        ]"
      />
    </div>

    <div class="col-xs-7 col-sm-4">
      <q-select
        ref="expirationMonth"
        v-model="expirationMonth"
        outlined
        map-options
        emit-value
        autocomplete="cc-exp-month"
        :options="monthOptions"
        :label="$t('payment.expirationMonth')"
        :rules="[
          v => !!v || $t('payment.validation.expirationMonth.required'),
          () => validateExpiration || $t('payment.validation.expirationMonth.invalid')
        ]"
      />
    </div>

    <div class="col-xs-5 col-sm-4">
      <q-select
        ref="expirationYear"
        v-model="expirationYear"
        outlined
        map-options
        emit-value
        autocomplete="cc-exp-year"
        :options="yearOptions"
        :label="$t('payment.expirationYear')"
        :rules="[
          v => !!v || $t('payment.validation.expirationYear.required'),
          () => validateExpiration || $t('payment.validation.expirationYear.invalid')
        ]"
      />
    </div>

    <div class="col-xs-6 col-sm-5 col-md-4">
      <q-input
        ref="ccv"
        v-model="ccv"
        outlined
        autocomplete="cc-csc"
        type="tel"
        mask="####"
        :label="$t('payment.ccv')"
        :rules="[
          v => !!v || $t('payment.validation.ccv.required'),
          v => v && v.length >= 3 || $t('payment.validation.ccv.required'),
        ]"
      />
    </div>
  </q-form>
</template>

<script>
import { debounce } from 'quasar';
import { mapMutations, mapState } from 'vuex';
import cardValidator from 'card-validator';

export default {
  name: 'PaymentCardForm',

  data() {
    return {
      cardNumber: '',
      ccv: '',
      expirationMonth: '',
      expirationYear: '',
      monthOptions: [
        { label: `01 - ${this.$t('form.months.jan')}`, value: '01' },
        { label: `02 - ${this.$t('form.months.feb')}`, value: '02' },
        { label: `03 - ${this.$t('form.months.mar')}`, value: '03' },
        { label: `04 - ${this.$t('form.months.apr')}`, value: '04' },
        { label: `05 - ${this.$t('form.months.mai')}`, value: '05' },
        { label: `06 - ${this.$t('form.months.jun')}`, value: '06' },
        { label: `07 - ${this.$t('form.months.jul')}`, value: '07' },
        { label: `08 - ${this.$t('form.months.aug')}`, value: '08' },
        { label: `09 - ${this.$t('form.months.sep')}`, value: '09' },
        { label: `10 - ${this.$t('form.months.oct')}`, value: '10' },
        { label: `11 - ${this.$t('form.months.nov')}`, value: '11' },
        { label: `12 - ${this.$t('form.months.dec')}`, value: '12' },
      ],
      yearOptions: [],
    };
  },

  computed: {
    ...mapState('order', ['paymentData']),
    validateCardNumber() {
      const valid = cardValidator.number(this.cardNumber);
      return valid.isPotentiallyValid;
    },
    validateExpiration() {
      if (this.expirationMonth && this.expirationYear) {
        const validMonth = cardValidator.expirationMonth(this.expirationMonth);
        const validYear = cardValidator.expirationYear(this.expirationYear);

        if (validYear.isCurrentYear && !validMonth.isValidForThisYear) {
          return false;
        }

        return validMonth.isValid && validYear.isValid;
      }
      return true;
    },
  },

  watch: {
    cardNumber() {
      this.$nextTick(this.submitForm);
    },
    ccv() {
      this.$nextTick(this.submitForm);
    },
    expirationMonth() {
      this.$nextTick(this.submitForm);
    },
    expirationYear() {
      this.$nextTick(this.submitForm);
    },
  },

  created() {
    // Debouncing because of validation
    this.submitForm = debounce(this.submitForm, 200);

    this.createYearOptions();
    if (this.paymentData) {
      this.cardNumber = this.paymentData.cardNumber;
      this.expirationYear = this.paymentData.expiration ? `20${this.paymentData.expiration.substr(0, 2)}` : '';
      this.expirationMonth = this.paymentData.expiration ? this.paymentData.expiration.substr(2, 2) : '';
      this.ccv = this.paymentData.ccv;
    }
  },

  methods: {
    ...mapMutations('order', ['updatePaymentData']),

    validateForm() {
      this.$refs.expirationMonth.validate();
      this.$refs.expirationYear.validate();

      return !this.$refs.cardNumber.hasError
        && !this.$refs.expirationMonth.hasError
        && !this.$refs.expirationYear.hasError
        && !this.$refs.ccv.hasError;
    },

    submitForm() {
      if (this.validateForm()) {
        this.updatePaymentData({
          cardNumber: this.cardNumber && this.cardNumber.length > 0 ? this.cardNumber.replaceAll(' - ', '') : '',
          expirationMonth: this.expirationMonth,
          expirationYear: this.expirationYear,
          ccv: this.ccv,
        });
      }
    },

    createYearOptions() {
      const date = new Date();
      const currentYear = date.getFullYear();

      for (let i = currentYear; i < currentYear + 10; i += 1) {
        this.yearOptions.push({
          label: i,
          value: i.toString(),
        });
      }
    },
  },
};
</script>
